import { useParams } from 'react-router-dom'

import { SubprojectsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  CreateSubprojectDocument,
  ProjectManagementProjectDetailsPageAddSubprojectModalPageDocument,
  SubprojectsDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import AddEditSubprojectModal, {
  AddEditSubprojectFormValues,
} from 'modules/project_management/components/modals/AddEditSubprojectModal'

const AddSubprojectModalPage = () => {
  const { id } = useParams()
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading: loadingPolicies } = useQuery(
    ProjectManagementProjectDetailsPageAddSubprojectModalPageDocument,
    {
      variables: {
        companyId,
      },
    }
  )

  const dataCollectionsEnabled =
    data?.features.featuresConnection.nodes.find(
      (node) => node.name === 'dev_projects_data_collections'
    )?.enabled ?? false

  const canCreateSubproject = data?.projectManagement._policies.subprojectManage.can

  const [createSubproject, { loading }] = useMutation(CreateSubprojectDocument, {
    extractorFn: (mutation) =>
      mutation.projectManagementMutations.createProjectmanagementSubproject,
    refetchQueries: [
      dataCollectionsEnabled ? SubprojectsDataCollectionDocument : SubprojectsDataTableDocument,
    ],
    awaitRefetchQueries: true,
  })

  if (loadingPolicies || !canCreateSubproject) return null

  const onSubmit = ({ name }: AddEditSubprojectFormValues) => {
    track(trackingEvents.projectManagement.subprojects.addSubprojectSubmit, {
      project_id: id,
    })

    return createSubproject({ variables: { name: name, projectId: Number(id) } })
  }

  return (
    <AddEditSubprojectModal
      onSubmit={onSubmit}
      loading={loading}
      title={i18n.t('project_management.modals.create_subproject.title')}
      description={i18n.t('project_management.modals.create_subproject.description')}
      iconName={'document-edit'}
      nameLabel={i18n.t('project_management.modals.create_subproject.input_label')}
      submitButton={i18n.t('project_management.modals.create_subproject.submit')}
    />
  )
}

export default AddSubprojectModalPage
